import * as Types from '@frameio/graphql-client/generated/types';

import { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
import gql from 'graphql-tag';
import { CompleteCommentAttachment } from './CompleteCommentAttachment';
import { CompleteImages } from './CompleteImages';

import { useQuery, useLazyQuery, useMutation, useFragment, useSubscription } from '@apollo/client';
import type { QueryHookOptions, LazyQueryHookOptions, MutationHookOptions, SubscriptionHookOptions, UseFragmentOptions } from '@apollo/client';

export type CompleteCommentFragment = { __typename?: 'Comment', skipSubscriptionUpdate?: boolean | null, deleted?: boolean | null, draftId?: string | null, failedToSend?: Types.OptimisticCommentFailure | null, optimisticId?: string | null, id: string, annotation?: string | null, assetId: string, completed?: boolean | null, duration?: number | null, isCopy?: boolean | null, number?: number | null, text?: string | null, timestampMicroseconds?: number | null, insertedAt?: string | null, editedAt?: string | null, readCount?: number | null, readByViewer?: boolean | null, parentId?: string | null, documentPageNumber?: number | null, private?: boolean | null, attachments?: Array<{ __typename?: 'CommentAttachment', filetype?: string | null, filesize?: number | null, id: string, insertedAt?: string | null, name?: string | null, commentId?: string | null, order?: number | null, media?: { __typename?: 'AudioMedia', id: string, mimeType?: string | null, transcodedAt?: string | null, duration?: number | null, filesize?: number | null, fps?: number | null, hlsManifest?: string | null, insertedAt?: string | null, checksums?: { __typename?: 'AssetChecksums', xxHash?: string | null } | null, audioTranscodes?: Array<{ __typename?: 'AudioTranscodeInfo', downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, key: string, streamUrl?: string | null }> | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null } | { __typename?: 'DocumentMedia', id: string, mimeType?: string | null, transcodedAt?: string | null, filesize?: number | null, insertedAt?: string | null, pageCount?: number | null, checksums?: { __typename?: 'AssetChecksums', xxHash?: string | null } | null, metadata?: { __typename?: 'DocumentMetadata', id?: string | null, originalHeight?: number | null, originalWidth?: number | null, baseUrl?: string | null } | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null, pages: Array<{ __typename?: 'Page', height?: number | null, width?: number | null }> } | { __typename?: 'ImageMedia', id: string, mimeType?: string | null, transcodedAt?: string | null, filesize?: number | null, insertedAt?: string | null, checksums?: { __typename?: 'AssetChecksums', xxHash?: string | null } | null, imageTranscodes?: Array<{ __typename?: 'ImageTranscodeInfo', downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, key: string, streamUrl?: string | null }> | null, metadata?: { __typename?: 'ImageMetadata', id?: string | null, originalHeight?: number | null, originalWidth?: number | null, hasAlpha?: boolean | null } | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null } | { __typename?: 'InteractiveLiveMedia', id: string, mimeType?: string | null, transcodedAt?: string | null, checksums?: { __typename?: 'AssetChecksums', xxHash?: string | null } | null } | { __typename?: 'InteractiveMedia', id: string, mimeType?: string | null, transcodedAt?: string | null, checksums?: { __typename?: 'AssetChecksums', xxHash?: string | null } | null } | { __typename?: 'ModelMedia', id: string, mimeType?: string | null, transcodedAt?: string | null, checksums?: { __typename?: 'AssetChecksums', xxHash?: string | null } | null } | { __typename?: 'UnsupportedMedia', id: string, mimeType?: string | null, transcodedAt?: string | null, filesize?: number | null, insertedAt?: string | null, checksums?: { __typename?: 'AssetChecksums', xxHash?: string | null } | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null } | { __typename?: 'VideoMedia', id: string, mimeType?: string | null, transcodedAt?: string | null, duration?: number | null, filesize?: number | null, fps?: number | null, frames?: number | null, hlsManifest?: string | null, insertedAt?: string | null, timecode?: string | null, checksums?: { __typename?: 'AssetChecksums', xxHash?: string | null } | null, drm?: { __typename?: 'Drm', fairplayStreamingCertificateUrl?: string | null, fairplayStreamingLicenseUrl?: string | null, widevineLicenseUrl?: string | null } | null, imageTranscodes?: Array<{ __typename?: 'ImageTranscodeInfo', downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, key: string, streamUrl?: string | null }> | null, metadata?: { __typename?: 'VideoMetadata', id?: string | null, originalHeight?: number | null, originalWidth?: number | null, isVfr?: boolean | null } | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null, videoTranscodes?: Array<{ __typename?: 'VideoTranscodeInfo', codec?: string | null, downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, filesizeInBytes?: number | null, height?: number | null, key: string, streamUrl?: string | null, required: boolean, width?: number | null }> | null } | null }> | null, bounds?: { __typename?: 'CommentBounds', x: number, y: number } | null, completedBy?: { __typename?: 'AuthenticatedUser', id: string, name?: string | null } | null, deviceLinkChannelInput?: { __typename?: 'DeviceLinkChannelInput', id?: string | null } | null, textReviewAnnotation?: { __typename?: 'TextReviewAnnotation', selectionText?: string | null, suggestedText?: string | null, textReviewAnnotationType: Types.TextReviewAnnotationType, bounds: Array<{ __typename?: 'Rectangle', x: number, y: number, width: number, height: number }> } | null, readImpressions?: { __typename?: 'PaginatedReadImpressions', result: Array<{ __typename?: 'ReadImpression', type?: string | null, user?: { __typename?: 'AuthenticatedUser', id: string } | null }> } | null, owner?: { __typename?: 'AuthenticatedUser', email: string, id: string, name?: string | null, avatarColor?: Types.AvatarColor | null, profileImage?: { __typename?: 'Images', image32?: string | null, image64?: string | null, image128?: string | null, image256?: string | null, imageBase?: string | null, original?: string | null } | null } | { __typename?: 'IdentifiedUser', email: string, id: string, name?: string | null, avatarColor?: Types.AvatarColor | null } | null, commentEntities: Array<{ __typename?: 'CommentHashtag', text?: string | null, id: string, type?: string | null, endIndex: number, startIndex: number } | { __typename?: 'CommentLink', text?: string | null, url?: string | null, id: string, type?: string | null, endIndex: number, startIndex: number } | { __typename?: 'CommentMention', mentionText?: string | null, userId: string, id: string, type?: string | null, endIndex: number, startIndex: number } | null>, reactionCounts?: Array<{ __typename?: 'ReactionCount', shortcode: string, firstReactionAt?: string | null, creatorNames?: Array<string> | null, count?: number | null, requestingUserReacted: boolean }> | null, viewerContext?: { __typename?: 'ViewerContext', resolutionName?: string | null, url?: string | null, scrollPosition?: { __typename?: 'ViewerContextScrollPosition', height?: number | null, width?: number | null, x?: number | null, y?: number | null } | null } | null };

export const CompleteComment = /*#__PURE__*/ gql`
    fragment CompleteComment on Comment {
  skipSubscriptionUpdate @client
  deleted @client
  draftId @client
  failedToSend @client
  optimisticId @client
  id
  attachments {
    ...CompleteCommentAttachment
  }
  annotation
  assetId
  bounds {
    x
    y
  }
  completed
  completedBy {
    id
    name
  }
  deviceLinkChannelInput {
    id
  }
  duration
  isCopy
  number
  text
  textReviewAnnotation {
    bounds {
      x
      y
      width
      height
    }
    selectionText
    suggestedText
    textReviewAnnotationType
  }
  timestampMicroseconds
  insertedAt
  editedAt
  readCount
  readByViewer
  readImpressions {
    result {
      type
      user {
        id
      }
    }
  }
  parentId
  documentPageNumber
  owner {
    email
    id
    name
    avatarColor
    ... on AuthenticatedUser {
      profileImage {
        ...CompleteImages
      }
    }
  }
  commentEntities {
    id
    type
    ... on CommentLink {
      text
      url
    }
    ... on CommentMention {
      mentionText
      userId
    }
    ... on CommentHashtag {
      text
    }
    endIndex
    startIndex
  }
  reactionCounts {
    shortcode
    firstReactionAt
    creatorNames
    count
    requestingUserReacted
  }
  viewerContext {
    resolutionName
    scrollPosition {
      height
      width
      x
      y
    }
    url
  }
  private
}
    ${CompleteCommentAttachment}
${CompleteImages}` as unknown as DocumentNode<CompleteCommentFragment, unknown>;

        

        
export const useCompleteCommentFragment = (
  input: string | Omit<UseFragmentOptions<CompleteCommentFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Comment' } };
    return input;
  })();

  return useFragment<CompleteCommentFragment>({
    fragment: CompleteComment,
    fragmentName: 'CompleteComment',
    ...options,
  });
};
  
        